import React, { FC, useState, useEffect } from 'react';
import { Grid, useTheme, useMediaQuery, Skeleton, Autocomplete, createFilterOptions } from '@mui/material';
import { AccountBalance, Badge, ListAlt } from '@mui/icons-material';
import '../../App.css';
import Card from '../general/Card';
import SimpleCard from '../general/SimpleCard';
import {
  capitalize,
  compareCaseInsensitive,
  filterDigitsHyphens,
  format,
  formatCurrency,
  formatISODate,
} from '../../common/utils/common.util';
import { handleError } from '../../common/utils/util.errors';
import { useNavigate } from 'react-router-dom';
import { Typography, Box } from 'components/index';
import {
  FileUploadErrorDTO,
  ProjectSummary as ProjectSummaryDTO,
  constructionTypeOptions,
  electricalOptions,
  exteriorWallsOptions,
  foundationOptions,
  hvacScopeOptions,
  hvacTypeOptions,
  plumbingOptions,
  roofOptions,
  superStructureOptions,
  uniqueCostFactors1Options,
  uniqueCostFactors2Options,
  uniqueCostFactors3Options,
  windowsOptions,
} from './project.type';
import NumericFormatInput from '../general/NumericFormatInput';
import { getErrorFieldNames } from './project.util';
import CustomizedTextField from './CustomizedTextField';
import useLookups from '../../hooks/useLookups';
import { LOOKUP_KEYS } from '../../common/constants/lookups';
import { City, State } from '../lookups/lookups.type';
import { getCityString, getStateLabel } from '../lookups/lookups.util';
import useCityStateValidation from '../../hooks/useCityStateValidation';
import DynamicDatePicker from '../common/DynamicDatePicker';
const DEBOUNCE_MS = 500;

interface ProjectSummaryProps {
  project?: ProjectSummaryDTO;
  isLoading: boolean;
  isError: boolean;
  error: any;
  isEditMode: boolean;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  onEditChange: (field: string, value: any) => void;
  projectErrors: FileUploadErrorDTO[];
  readonly?: boolean;
}

const ProjectSummary: FC<ProjectSummaryProps> = ({
  project,
  isLoading,
  isError,
  error,
  isEditMode,
  setIsEditMode,
  onEditChange,
  projectErrors,
  readonly,
}) => {
  const costFactors = [project?.uniqueCostFactors1, project?.uniqueCostFactors2, project?.uniqueCostFactors3].filter(Boolean).join(', ');
  const navigate = useNavigate();
  const theme = useTheme();
  const isScreenBelowMedium = useMediaQuery(theme.breakpoints.down('md'));
  const [clientPostalCode, setClientPostalCode] = useState('');
  const [borrowerPostalCode, setBorrowerPostalCode] = useState('');
  const [clientSelectedState, setClientSelectedState] = useState('');
  const [borrowerSelectedState, setBorrowerSelectedState] = useState('');
  const [clientCity, setClientCity] = useState('');
  const [borrowerCity, setBorrowerCity] = useState('');
  const [clientCitySelectedFromList, setClientCitySelectedFromList] = useState(false);
  const [borrowerCitySelectedFromList, setBorrowerCitySelectedFromList] = useState(false);
  const [clientCityDraft, setClientCityDraft] = useState<string | null>(null);
  const [borrowerCityDraft, setBorrowerCityDraft] = useState<string | null>(null);
  // Local drafts so keystrokes update the numeric "Other" section fields instantly without
  // pushing into the shared project state on every keystroke. Only committed via onEditChange
  // on blur. Seeded from and re-synced with the actual project value (not undefined), so
  // blurring a field without typing in it commits the unchanged value instead of clearing it.
  const [monitoringFeeDraft, setMonitoringFeeDraft] = useState(project?.monitoringFee);
  const [planOrCostFeeDraft, setPlanOrCostFeeDraft] = useState(project?.planOrCostFee);
  const [siteAreaDraft, setSiteAreaDraft] = useState(project?.siteArea);
  const [storyCountDraft, setStoryCountDraft] = useState(project?.storyCount);
  const [buildingCountDraft, setBuildingCountDraft] = useState(project?.buildingCount);
  const [unitCountDraft, setUnitCountDraft] = useState(project?.unitCount);
  const [elevatorCountDraft, setElevatorCountDraft] = useState(project?.elevatorCount);
  const [networkerNameDraft, setNetworkerNameDraft] = useState(project?.networkerName);
  const [singleChangeOrderThresholdDraft, setSingleChangeOrderThresholdDraft] = useState<string | number | undefined>(
    project?.singleChangeOrderThreshold,
  );
  const [aggregateChangeOrderThresholdDraft, setAggregateChangeOrderThresholdDraft] = useState<string | number | undefined>(
    project?.aggregateChangeOrderThreshold,
  );
  useEffect(() => {
    setMonitoringFeeDraft(project?.monitoringFee);
    setPlanOrCostFeeDraft(project?.planOrCostFee);
    setSiteAreaDraft(project?.siteArea);
    setStoryCountDraft(project?.storyCount);
    setBuildingCountDraft(project?.buildingCount);
    setUnitCountDraft(project?.unitCount);
    setElevatorCountDraft(project?.elevatorCount);
    setNetworkerNameDraft(project?.networkerName);
    setSingleChangeOrderThresholdDraft(project?.singleChangeOrderThreshold);
    setAggregateChangeOrderThresholdDraft(project?.aggregateChangeOrderThreshold);
  }, [
    project?.monitoringFee,
    project?.planOrCostFee,
    project?.siteArea,
    project?.storyCount,
    project?.buildingCount,
    project?.unitCount,
    project?.elevatorCount,
    project?.networkerName,
    project?.singleChangeOrderThreshold,
    project?.aggregateChangeOrderThreshold,
  ]);
  const stateOptions = useLookups(LOOKUP_KEYS.States) as State[];
  const cityOptions = useLookups(LOOKUP_KEYS.Cities) as City[];
  const cityOptionStrings = cityOptions.map(getCityString);
  // Kept separate per block (not merged into errorFieldNames) so a Borrower error never
  // highlights the Client fields, and vice versa.
  const { status: clientCityStateStatus, checkOnBlur: checkClientCityStateOnBlur } = useCityStateValidation(
    cityOptions,
    clientCitySelectedFromList ? null : (clientCityDraft ?? project?.client?.address?.city),
    clientSelectedState,
  );
  const { status: borrowerCityStateStatus, checkOnBlur: checkBorrowerCityStateOnBlur } = useCityStateValidation(
    cityOptions,
    borrowerCitySelectedFromList ? null : (borrowerCityDraft ?? project?.borrower?.address?.city),
    borrowerSelectedState,
  );
  const isClientCityStateInvalid = !clientCitySelectedFromList && clientCityStateStatus === 'invalid';
  const isBorrowerCityStateInvalid = !borrowerCitySelectedFromList && borrowerCityStateStatus === 'invalid';
  const isClientCityStateChecking = !clientCitySelectedFromList && clientCityStateStatus === 'checking';
  const isBorrowerCityStateChecking = !borrowerCitySelectedFromList && borrowerCityStateStatus === 'checking';
  const errorFieldNames = getErrorFieldNames(projectErrors);

  const typographyHeadStyles = {
    variant: 'caption',
    color: 'text',
    fontWeight: 'medium',
    textTransform: 'capitalize',
  };
  const typographySubStyles = {
    variant: 'body2',
    fontWeight: 'regular',
    sx: { whiteSpace: 'pre-wrap' },
  };

  useEffect(() => {
    if (project?.client?.address?.postalCode) {
      setClientPostalCode(project.client.address.postalCode);
    }
    if (project?.borrower?.address?.postalCode) {
      setBorrowerPostalCode(project?.borrower?.address?.postalCode);
    }
    // Handle Client Address State
    if (project?.client?.address?.state) {
      let matchingState = stateOptions.find(option => compareCaseInsensitive(option.stateCode, project?.client?.address?.state ?? ''));

      if (!matchingState) {
        matchingState = stateOptions.find(option => compareCaseInsensitive(option.name, project?.client?.address?.state ?? ''));
      }
      setClientSelectedState(matchingState ? (capitalize(matchingState.name) ?? '') : project.client.address.state);
    }

    // Handle Borrower Address State
    if (project?.borrower?.address?.state) {
      let matchingState = stateOptions.find(option => compareCaseInsensitive(option.stateCode, project?.borrower?.address?.state ?? ''));

      if (!matchingState) {
        matchingState = stateOptions.find(option => compareCaseInsensitive(option.name, project?.borrower?.address?.state ?? ''));
      }
      setBorrowerSelectedState(matchingState ? (capitalize(matchingState.name) ?? '') : project.borrower.address.state);
    }
  }, [project, stateOptions]);

  useEffect(() => {
    if (clientCity) {
      const stateCode = clientCity.split(', ')[1];
      const matchingState = stateOptions.find(option => compareCaseInsensitive(option.stateCode, stateCode));
      setClientSelectedState(capitalize(matchingState?.name) ?? '');
      onEditChange('client.address.state', capitalize(matchingState?.name) ?? '');
    } else {
      setClientSelectedState('');
    }
  }, [clientCity]);

  useEffect(() => {
    if (borrowerCity) {
      const stateCode = borrowerCity.split(', ')[1];
      const matchingState = stateOptions.find(option => compareCaseInsensitive(option.stateCode, stateCode));
      setBorrowerSelectedState(capitalize(matchingState?.name) ?? '');
      onEditChange('borrower.address.state', capitalize(matchingState?.name) ?? '');
    } else {
      setBorrowerSelectedState('');
    }
  }, [borrowerCity]);

  if (isError) {
    handleError(error);
    navigate('./NotFound'); // Use the navigate function to go to the 404 page
    return null;
  }
  if (isLoading)
    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: 6 }}>
          <Skeleton variant='rounded' width={'100%'} height={120} />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Skeleton variant='rounded' width={'100%'} height={120} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Skeleton variant='rounded' width={'100%'} height={240} />
        </Grid>
      </Grid>
    );
  if (!project) return null;

  return (
    <Box>
      <Grid container spacing={3} alignItems='stretch'>
        <Grid size={{ xs: 12, lg: 6 }}>
          <SimpleCard
            icon={<AccountBalance color='primary' />}
            heading="Client's Information"
            isEditMode={isEditMode}
            showEditIcon={true}
            onEditClick={() => setIsEditMode(true)}
            disableActions={readonly}
            alignItems='left'
          >
            {isEditMode ? (
              <Grid container spacing={1}>
                <Grid size={{ xs: 6 }}>
                  <Typography {...typographyHeadStyles}>Client Name:</Typography>
                  <CustomizedTextField
                    placeholder='e.g., John Doe'
                    defaultValue={project?.client?.name}
                    onBlur={e => onEditChange('client.name', e.target.value)}
                    type='text'
                    fullWidth
                    hasError={errorFieldNames.includes('Client Name')}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography {...typographyHeadStyles}>Loan Officer Name:</Typography>
                  <CustomizedTextField
                    placeholder='e.g., John Doe'
                    defaultValue={project?.loanOfficerName}
                    onBlur={e => onEditChange('loanOfficerName', e.target.value)}
                    type='text'
                    fullWidth
                    hasError={errorFieldNames.includes('Loan Officer Name')}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Typography {...typographyHeadStyles}>Address:</Typography>
                  <CustomizedTextField
                    placeholder='Street'
                    defaultValue={project?.client?.address?.street}
                    onBlur={e => onEditChange('client.address.street', e.target.value)}
                    type='text'
                    fullWidth
                    hasError={errorFieldNames.includes('Address')}
                  />
                </Grid>

                <Grid size={{ xs: 4 }}>
                  <Typography {...typographyHeadStyles}>City:</Typography>
                  <Autocomplete
                    freeSolo
                    options={cityOptionStrings}
                    getOptionLabel={option => (typeof option === 'string' ? option : option)}
                    value={clientCityDraft ?? project?.client?.address?.city ?? ''}
                    onInputChange={(_, value) => {
                      setClientCity('');
                      setClientCitySelectedFromList(false);
                      setClientCityDraft(value ?? '');
                    }}
                    onChange={(event, newValue) => {
                      const isOptionFromList = newValue && typeof newValue === 'string' && cityOptionStrings.includes(newValue);
                      if (isOptionFromList && newValue) {
                        const cityName = newValue.split(', ')[0] ?? '';
                        const stateCode = newValue.split(', ')[1] ?? '';
                        const matchingState = stateOptions.find(option => compareCaseInsensitive(option.stateCode, stateCode));
                        const stateName = capitalize(matchingState?.name) ?? '';
                        setClientCity(newValue);
                        setClientCityDraft(null);
                        setClientSelectedState(stateName);
                        onEditChange('client.address.city', cityName);
                        onEditChange('client.address.state', stateName);
                        setClientCitySelectedFromList(true);
                      } else {
                        setClientCity('');
                        setClientCitySelectedFromList(false);
                        if (newValue !== null) {
                          setClientCityDraft(typeof newValue === 'string' ? newValue : '');
                          onEditChange('client.address.city', typeof newValue === 'string' ? newValue : '');
                        }
                      }
                    }}
                    filterOptions={createFilterOptions({ limit: 10, matchFrom: 'start' })}
                    renderInput={params => (
                      <CustomizedTextField
                        {...params}
                        placeholder='City'
                        inputProps={{
                          ...params.inputProps,
                          onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                            params.inputProps?.onBlur?.(e);
                            const valueToSave = clientCityDraft ?? project?.client?.address?.city ?? '';
                            if (valueToSave !== (project?.client?.address?.city ?? '')) {
                              onEditChange('client.address.city', valueToSave);
                            }
                            setClientCityDraft(null);
                            checkClientCityStateOnBlur();
                          },
                        }}
                        sx={{ '& .MuiInputBase-input': { height: '10px', fontSize: '0.875rem', padding: '0px 0px' } }}
                        fullWidth
                        hasError={errorFieldNames.includes('City') || isClientCityStateInvalid}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Typography {...typographyHeadStyles}>State:</Typography>
                  {clientCitySelectedFromList ? (
                    <CustomizedTextField
                      value={clientSelectedState}
                      placeholder='State'
                      type='text'
                      fullWidth
                      inputProps={{ readOnly: true }}
                      sx={{ pointerEvents: 'none' }}
                      hasError={errorFieldNames.includes('State') || isClientCityStateInvalid}
                    />
                  ) : (
                    <Autocomplete
                      freeSolo
                      options={stateOptions}
                      getOptionLabel={option => (typeof option === 'string' ? option : (getStateLabel(option) ?? ''))}
                      value={clientSelectedState}
                      inputValue={clientSelectedState}
                      onInputChange={(_, newInputValue, reason) => {
                        if (reason === 'input') {
                          setClientSelectedState(newInputValue);
                        }
                      }}
                      onChange={(_, newValue) => {
                        const stateName = typeof newValue === 'string' ? newValue : newValue ? (getStateLabel(newValue) ?? '') : '';
                        setClientSelectedState(stateName);
                        onEditChange('client.address.state', stateName);
                      }}
                      filterOptions={createFilterOptions({ limit: 10, matchFrom: 'start' })}
                      renderInput={params => (
                        <CustomizedTextField
                          {...params}
                          placeholder='State'
                          sx={{ '& .MuiInputBase-input': { height: '10px', fontSize: '0.875rem', padding: '0px 0px' } }}
                          fullWidth
                          hasError={errorFieldNames.includes('State') || isClientCityStateInvalid}
                          onBlur={() => {
                            onEditChange('client.address.state', clientSelectedState);
                            checkClientCityStateOnBlur();
                          }}
                          helperText={
                            isClientCityStateInvalid
                              ? 'This city/state combination does not exist'
                              : isClientCityStateChecking
                                ? 'Verifying city/state…'
                                : ''
                          }
                        />
                      )}
                    />
                  )}
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Typography {...typographyHeadStyles}>Postal Code:</Typography>
                  <CustomizedTextField
                    placeholder='Postal Code'
                    value={clientPostalCode}
                    onChange={e => setClientPostalCode(filterDigitsHyphens(e.target.value))}
                    onBlur={() => onEditChange('client.address.postalCode', clientPostalCode)}
                    type='text'
                    fullWidth
                    hasError={errorFieldNames.includes('Postal Code')}
                  />
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={1}>
                <Grid size={{ xs: 6 }}>
                  <Typography {...typographyHeadStyles}>Client Name:</Typography>
                  <Typography {...typographySubStyles}>{project?.client?.name || '--'}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography {...typographyHeadStyles}>Loan Officer Name:</Typography>
                  <Typography {...typographySubStyles}>{project?.loanOfficerName || '--'}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography {...typographyHeadStyles}>Address:</Typography>
                  <Typography {...typographySubStyles}>{project?.client?.address?.street || '--'}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography {...typographyHeadStyles}>City/State/Zip:</Typography>
                  <Typography {...typographySubStyles}>
                    {project?.client?.address
                      ? [project?.client?.address?.city, project?.client?.address?.state, project?.client?.address?.postalCode]
                          .filter(Boolean)
                          .join(', ') || '--'
                      : '--'}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </SimpleCard>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <SimpleCard
            icon={<Badge color='primary' />}
            heading="Borrower's Information"
            isEditMode={isEditMode}
            showEditIcon={true}
            onEditClick={() => setIsEditMode(true)}
            disableActions={readonly}
            alignItems='left'
          >
            {isEditMode ? (
              <Grid container spacing={1}>
                <Grid size={{ xs: 6 }}>
                  <Typography {...typographyHeadStyles}>Borrower Name:</Typography>
                  <CustomizedTextField
                    placeholder='e.g., John Doe'
                    defaultValue={project?.borrower?.name}
                    onBlur={e => onEditChange('borrower.name', e.target.value)}
                    type='text'
                    fullWidth
                    hasError={errorFieldNames.includes('Borrower Name')}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography {...typographyHeadStyles}>Company:</Typography>
                  <CustomizedTextField
                    placeholder='e.g., XYZ Corp'
                    defaultValue={project?.borrower?.company}
                    onBlur={e => onEditChange('borrower.company', e.target.value)}
                    type='text'
                    fullWidth
                    hasError={errorFieldNames.includes('Company')}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Typography {...typographyHeadStyles}>Address:</Typography>
                  <CustomizedTextField
                    placeholder='Street'
                    defaultValue={project?.borrower?.address?.street}
                    onBlur={e => onEditChange('borrower.address.street', e.target.value)}
                    type='text'
                    fullWidth
                    hasError={errorFieldNames.includes('Street')}
                  />
                </Grid>

                <Grid size={{ xs: 4 }}>
                  <Typography {...typographyHeadStyles}>City:</Typography>
                  <Autocomplete
                    freeSolo
                    options={cityOptionStrings}
                    getOptionLabel={option => (typeof option === 'string' ? option : option)}
                    value={borrowerCityDraft ?? project?.borrower?.address?.city ?? ''}
                    onInputChange={(_, value) => {
                      setBorrowerCity('');
                      setBorrowerCitySelectedFromList(false);
                      setBorrowerCityDraft(value ?? '');
                    }}
                    onChange={(event, newValue) => {
                      const isOptionFromList = newValue && typeof newValue === 'string' && cityOptionStrings.includes(newValue);
                      if (isOptionFromList && newValue) {
                        const cityName = newValue.split(', ')[0] ?? '';
                        const stateCode = newValue.split(', ')[1] ?? '';
                        const matchingState = stateOptions.find(option => compareCaseInsensitive(option.stateCode, stateCode));
                        const stateName = capitalize(matchingState?.name) ?? '';
                        setBorrowerCity(newValue);
                        setBorrowerCityDraft(null);
                        setBorrowerSelectedState(stateName);
                        onEditChange('borrower.address.city', cityName);
                        onEditChange('borrower.address.state', stateName);
                        setBorrowerCitySelectedFromList(true);
                      } else {
                        setBorrowerCity('');
                        setBorrowerCitySelectedFromList(false);
                        if (newValue !== null) {
                          setBorrowerCityDraft(typeof newValue === 'string' ? newValue : '');
                          onEditChange('borrower.address.city', typeof newValue === 'string' ? newValue : '');
                        }
                      }
                    }}
                    filterOptions={createFilterOptions({ limit: 10, matchFrom: 'start' })}
                    renderInput={params => (
                      <CustomizedTextField
                        {...params}
                        placeholder='City'
                        inputProps={{
                          ...params.inputProps,
                          onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                            params.inputProps?.onBlur?.(e);
                            const valueToSave = borrowerCityDraft ?? project?.borrower?.address?.city ?? '';
                            if (valueToSave !== (project?.borrower?.address?.city ?? '')) {
                              onEditChange('borrower.address.city', valueToSave);
                            }
                            setBorrowerCityDraft(null);
                            checkBorrowerCityStateOnBlur();
                          },
                        }}
                        sx={{ '& .MuiInputBase-input': { height: '10px', fontSize: '0.875rem', padding: '0px 0px' } }}
                        fullWidth
                        hasError={errorFieldNames.includes('City') || isBorrowerCityStateInvalid}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Typography {...typographyHeadStyles}>State:</Typography>
                  {borrowerCitySelectedFromList ? (
                    <CustomizedTextField
                      value={borrowerSelectedState}
                      placeholder='State'
                      type='text'
                      fullWidth
                      inputProps={{ readOnly: true }}
                      sx={{ pointerEvents: 'none' }}
                      hasError={errorFieldNames.includes('State') || isBorrowerCityStateInvalid}
                    />
                  ) : (
                    <Autocomplete
                      freeSolo
                      options={stateOptions}
                      getOptionLabel={option => (typeof option === 'string' ? option : (getStateLabel(option) ?? ''))}
                      value={borrowerSelectedState}
                      inputValue={borrowerSelectedState}
                      onInputChange={(_, newInputValue, reason) => {
                        if (reason === 'input') {
                          setBorrowerSelectedState(newInputValue);
                        }
                      }}
                      onChange={(_, newValue) => {
                        const stateName = typeof newValue === 'string' ? newValue : newValue ? (getStateLabel(newValue) ?? '') : '';
                        setBorrowerSelectedState(stateName);
                        onEditChange('borrower.address.state', stateName);
                      }}
                      filterOptions={createFilterOptions({ limit: 10, matchFrom: 'start' })}
                      renderInput={params => (
                        <CustomizedTextField
                          {...params}
                          placeholder='State'
                          sx={{ '& .MuiInputBase-input': { height: '10px', fontSize: '0.875rem', padding: '0px 0px' } }}
                          fullWidth
                          hasError={errorFieldNames.includes('State') || isBorrowerCityStateInvalid}
                          onBlur={() => {
                            onEditChange('borrower.address.state', borrowerSelectedState);
                            checkBorrowerCityStateOnBlur();
                          }}
                          helperText={
                            isBorrowerCityStateInvalid
                              ? 'This city/state combination does not exist'
                              : isBorrowerCityStateChecking
                                ? 'Verifying city/state…'
                                : ''
                          }
                        />
                      )}
                    />
                  )}
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Typography {...typographyHeadStyles}>Postal Code:</Typography>
                  <CustomizedTextField
                    placeholder='Postal Code'
                    value={borrowerPostalCode}
                    onChange={e => setBorrowerPostalCode(filterDigitsHyphens(e.target.value))}
                    onBlur={() => onEditChange('borrower.address.postalCode', borrowerPostalCode)}
                    type='text'
                    fullWidth
                    hasError={errorFieldNames.includes('Postal Code')}
                  />
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={1}>
                <Grid size={{ xs: 6 }}>
                  <Typography {...typographyHeadStyles}>Borrower Name:</Typography>
                  <Typography {...typographySubStyles}>{project?.borrower?.name || '--'}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography {...typographyHeadStyles}>Company:</Typography>
                  <Typography {...typographySubStyles}>{project?.borrower?.company || '--'}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography {...typographyHeadStyles}>Address:</Typography>
                  <Typography {...typographySubStyles}>{project?.borrower?.address?.street || '--'}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography {...typographyHeadStyles}>City/State/Zip:</Typography>
                  <Typography {...typographySubStyles}>
                    {project?.borrower?.address
                      ? [project?.borrower?.address?.city, project?.borrower?.address?.state, project?.borrower?.address?.postalCode]
                          .filter(Boolean)
                          .join(', ') || '--'
                      : '--'}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </SimpleCard>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Card
            icon={<ListAlt color='primary' />}
            heading='Other'
            collapsible
            data={{
              Contractor: isEditMode ? (
                <CustomizedTextField
                  placeholder='e.g., XYZ Corp'
                  defaultValue={project?.contractor}
                  onBlur={e => onEditChange('contractor', e.target.value)}
                  type='text'
                  hasError={errorFieldNames.includes('Contractor')}
                />
              ) : (
                (project?.contractor ?? '--')
              ),
              'Networker Name': isEditMode ? (
                <CustomizedTextField
                  placeholder='e.g., John Doe'
                  value={networkerNameDraft ?? ''}
                  onChange={e => setNetworkerNameDraft(e.target.value)}
                  onBlur={e => {
                    onEditChange('networkerName', e.target.value);
                  }}
                  type='text'
                  hasError={errorFieldNames.includes('Networker Name')}
                />
              ) : (
                (project?.networkerName ?? '--')
              ),
              'Networker Status': isEditMode ? (
                <Autocomplete
                  options={['TRUE', 'FALSE', 'PARTIAL']}
                  value={project?.networkerStatus ?? (project?.networkerName ? 'TRUE' : 'FALSE')}
                  onChange={(event, newValue) => onEditChange('networkerStatus', newValue ?? 'FALSE')}
                  renderInput={params => (
                    <CustomizedTextField placeholder='Select status' {...params} hasError={errorFieldNames.includes('Networker Status')} />
                  )}
                />
              ) : (
                (project?.networkerStatus ?? (project?.networkerName ? 'TRUE' : 'FALSE'))
              ),
              'Monitoring Fee': isEditMode ? (
                <CustomizedTextField
                  placeholder='0'
                  value={monitoringFeeDraft}
                  onChange={e => setMonitoringFeeDraft(e.target.value)}
                  onBlur={() => onEditChange('monitoringFee', monitoringFeeDraft)}
                  InputProps={{
                    inputComponent: NumericFormatInput as any,
                  }}
                  hasError={errorFieldNames.includes('Monitoring Fee')}
                />
              ) : (
                format(project?.monitoringFee ?? '--', 'currency')
              ),
              'Plan / Cost Review Fee': isEditMode ? (
                <CustomizedTextField
                  placeholder='0'
                  value={planOrCostFeeDraft}
                  onChange={e => setPlanOrCostFeeDraft(e.target.value)}
                  onBlur={() => onEditChange('planOrCostFee', planOrCostFeeDraft)}
                  InputProps={{
                    inputComponent: NumericFormatInput as any,
                  }}
                  hasError={errorFieldNames.includes('Plan / Cost Review Fee')}
                />
              ) : (
                (project?.planOrCostFee ?? '--')
              ),
              'Plan / Cost Review Invoice Date': isEditMode ? (
                <DynamicDatePicker
                  fieldName='planOrCostInvDate'
                  value={project?.planOrCostInvDate ?? null}
                  onChange={onEditChange}
                  hasError={errorFieldNames.includes('Plan / Cost Review Invoice Date')}
                />
              ) : (
                formatISODate(project?.planOrCostInvDate) || '--'
              ),
              'Site Area (ac)': isEditMode ? (
                <CustomizedTextField
                  placeholder='0'
                  value={siteAreaDraft}
                  onChange={e => setSiteAreaDraft(e.target.value)}
                  onBlur={() => onEditChange('siteArea', siteAreaDraft)}
                  InputProps={{
                    inputComponent: NumericFormatInput as any,
                  }}
                  hasError={errorFieldNames.includes('Site Area (ac)')}
                />
              ) : (
                format(project?.siteArea ?? '--', 'number')
              ),
              'Story Count (most common)': isEditMode ? (
                <CustomizedTextField
                  placeholder='0'
                  value={storyCountDraft}
                  onChange={e => setStoryCountDraft(e.target.value)}
                  onBlur={() => onEditChange('storyCount', storyCountDraft)}
                  InputProps={{
                    inputComponent: NumericFormatInput as any,
                  }}
                  hasError={errorFieldNames.includes('Story Count (most common)')}
                />
              ) : (
                format(project?.storyCount ?? '--', 'number')
              ),
              'Building Count': isEditMode ? (
                <CustomizedTextField
                  placeholder='0'
                  value={buildingCountDraft}
                  onChange={e => setBuildingCountDraft(e.target.value)}
                  onBlur={() => onEditChange('buildingCount', buildingCountDraft)}
                  InputProps={{
                    inputComponent: NumericFormatInput as any,
                  }}
                  hasError={errorFieldNames.includes('Building Count')}
                />
              ) : (
                format(project?.buildingCount ?? '--', 'number')
              ),
              'Unit Count': isEditMode ? (
                <CustomizedTextField
                  placeholder='0'
                  value={unitCountDraft}
                  onChange={e => setUnitCountDraft(e.target.value)}
                  onBlur={() => onEditChange('unitCount', unitCountDraft)}
                  InputProps={{
                    inputComponent: NumericFormatInput as any,
                  }}
                  hasError={errorFieldNames.includes('Unit Count')}
                />
              ) : (
                format(project?.unitCount ?? '--', 'number')
              ),
              Foundation: isEditMode ? (
                <Autocomplete
                  options={foundationOptions}
                  getOptionLabel={option => option.label}
                  defaultValue={foundationOptions.find(option => option.label === project?.foundation)}
                  onChange={(event, newValue) => onEditChange('foundation', newValue ? newValue.label : '')}
                  renderInput={params => (
                    <CustomizedTextField placeholder='Select an option' {...params} hasError={errorFieldNames.includes('Foundation')} />
                  )}
                />
              ) : (
                (project?.foundation ?? '--')
              ),
              Superstructure: isEditMode ? (
                <Autocomplete
                  options={superStructureOptions}
                  getOptionLabel={option => option.label}
                  defaultValue={superStructureOptions.find(option => option.label === project?.superStructure)}
                  onChange={(event, newValue) => onEditChange('superStructure', newValue ? newValue.label : '')}
                  renderInput={params => (
                    <CustomizedTextField placeholder='Select an option' {...params} hasError={errorFieldNames.includes('Superstructure')} />
                  )}
                />
              ) : (
                (project?.superStructure ?? '--')
              ),
              'Exterior Walls': isEditMode ? (
                <Autocomplete
                  options={exteriorWallsOptions}
                  getOptionLabel={option => option.label}
                  defaultValue={exteriorWallsOptions.find(option => option.label === project?.exteriorWalls)}
                  onChange={(event, newValue) => onEditChange('exteriorWalls', newValue ? newValue.label : '')}
                  renderInput={params => (
                    <CustomizedTextField placeholder='Select an option' {...params} hasError={errorFieldNames.includes('Exterior Walls')} />
                  )}
                />
              ) : (
                (project?.exteriorWalls ?? '--')
              ),
              Roof: isEditMode ? (
                <Autocomplete
                  options={roofOptions}
                  getOptionLabel={option => option.label}
                  defaultValue={roofOptions.find(option => option.label === project?.roof)}
                  onChange={(event, newValue) => onEditChange('roof', newValue ? newValue.label : '')}
                  renderInput={params => (
                    <CustomizedTextField placeholder='Select an option' {...params} hasError={errorFieldNames.includes('Roof')} />
                  )}
                />
              ) : (
                (project?.roof ?? '--')
              ),
              'HVAC Scope': isEditMode ? (
                <Autocomplete
                  options={hvacScopeOptions}
                  getOptionLabel={option => option.label}
                  defaultValue={hvacScopeOptions.find(option => option.label === project?.hvacScope)}
                  onChange={(event, newValue) => onEditChange('hvacScope', newValue ? newValue.label : '')}
                  renderInput={params => (
                    <CustomizedTextField placeholder='Select an option' {...params} hasError={errorFieldNames.includes('HVAC Scope')} />
                  )}
                />
              ) : (
                (project?.hvacScope ?? '--')
              ),
              'HVAC Type': isEditMode ? (
                <Autocomplete
                  options={hvacTypeOptions}
                  getOptionLabel={option => option.label}
                  defaultValue={hvacTypeOptions.find(option => option.label === project?.hvacType)}
                  onChange={(event, newValue) => onEditChange('hvacType', newValue ? newValue.label : '')}
                  renderInput={params => (
                    <CustomizedTextField placeholder='Select an option' {...params} hasError={errorFieldNames.includes('HVAC Type')} />
                  )}
                />
              ) : (
                (project?.hvacType ?? '--')
              ),
              Plumbing: isEditMode ? (
                <Autocomplete
                  options={plumbingOptions}
                  getOptionLabel={option => option.label}
                  defaultValue={plumbingOptions.find(option => option.label === project?.plumbing)}
                  onChange={(event, newValue) => onEditChange('plumbing', newValue ? newValue.label : '')}
                  renderInput={params => (
                    <CustomizedTextField placeholder='Select an option' {...params} hasError={errorFieldNames.includes('Plumbing')} />
                  )}
                />
              ) : (
                (project?.plumbing ?? '--')
              ),
              Electrical: isEditMode ? (
                <Autocomplete
                  options={electricalOptions}
                  getOptionLabel={option => option.label}
                  defaultValue={electricalOptions.find(option => option.label === project?.electrical)}
                  onChange={(event, newValue) => onEditChange('electrical', newValue ? newValue.label : '')}
                  renderInput={params => (
                    <CustomizedTextField placeholder='Select an option' {...params} hasError={errorFieldNames.includes('Electrical')} />
                  )}
                />
              ) : (
                (project?.electrical ?? '--')
              ),
              'No. of Elevator Stops': isEditMode ? (
                <CustomizedTextField
                  placeholder='0'
                  value={elevatorCountDraft}
                  onChange={e => setElevatorCountDraft(e.target.value)}
                  onBlur={() => onEditChange('elevatorCount', elevatorCountDraft)}
                  InputProps={{
                    inputComponent: NumericFormatInput as any,
                  }}
                  hasError={errorFieldNames.includes('No. of Elevator Stops')}
                />
              ) : (
                (project?.elevatorCount ?? '--')
              ),
              Windows: isEditMode ? (
                <Autocomplete
                  options={windowsOptions}
                  getOptionLabel={option => option.label}
                  defaultValue={windowsOptions.find(option => option.label === project?.windows)}
                  onChange={(event, newValue) => onEditChange('windows', newValue ? newValue.label : '')}
                  renderInput={params => (
                    <CustomizedTextField placeholder='Select an option' {...params} hasError={errorFieldNames.includes('Windows')} />
                  )}
                />
              ) : (
                (project?.windows ?? '--')
              ),
              'Unique Cost Factors 1': isEditMode ? (
                <Autocomplete
                  options={uniqueCostFactors1Options}
                  getOptionLabel={option => option.label}
                  defaultValue={uniqueCostFactors1Options.find(option => option.label === project?.uniqueCostFactors1)}
                  onChange={(event, newValue) => onEditChange('uniqueCostFactors1', newValue ? newValue.label : '')}
                  renderInput={params => (
                    <CustomizedTextField
                      placeholder='Select an option'
                      {...params}
                      hasError={errorFieldNames.includes('Unique Cost Factors 1')}
                    />
                  )}
                />
              ) : (
                (project?.uniqueCostFactors1 ?? '--')
              ),
              'Unique Cost Factors 2': isEditMode ? (
                <Autocomplete
                  options={uniqueCostFactors2Options}
                  getOptionLabel={option => option.label}
                  defaultValue={uniqueCostFactors2Options.find(option => option.label === project?.uniqueCostFactors2)}
                  onChange={(event, newValue) => onEditChange('uniqueCostFactors2', newValue ? newValue.label : '')}
                  renderInput={params => (
                    <CustomizedTextField
                      placeholder='Select an option'
                      {...params}
                      hasError={errorFieldNames.includes('Unique Cost Factors 2')}
                    />
                  )}
                />
              ) : (
                (project?.uniqueCostFactors2 ?? '--')
              ),
              'Unique Cost Factors 3': isEditMode ? (
                <Autocomplete
                  options={uniqueCostFactors3Options}
                  getOptionLabel={option => option.label}
                  defaultValue={uniqueCostFactors3Options.find(option => option.label === project?.uniqueCostFactors3)}
                  onChange={(event, newValue) => onEditChange('uniqueCostFactors3', newValue ? newValue.label : '')}
                  renderInput={params => (
                    <CustomizedTextField
                      placeholder='Select an option'
                      {...params}
                      hasError={errorFieldNames.includes('Unique Cost Factors 3')}
                    />
                  )}
                />
              ) : (
                (project?.uniqueCostFactors3 ?? '--')
              ),
              'Other Cost Factor': isEditMode ? (
                <CustomizedTextField
                  placeholder='Permit fees'
                  defaultValue={project?.otherCostFactor}
                  onBlur={e => onEditChange('otherCostFactor', e.target.value)}
                  type='text'
                  hasError={errorFieldNames.includes('Other Cost Factor')}
                />
              ) : (
                (project?.otherCostFactor ?? '--')
              ),
              'Plan or Cost Report Date': isEditMode ? (
                <DynamicDatePicker
                  fieldName='planOrCostReportDate'
                  value={project?.planOrCostReportDate ?? null}
                  onChange={onEditChange}
                  hasError={errorFieldNames.includes('Plan or Cost Report Date')}
                />
              ) : (
                formatISODate(project?.planOrCostReportDate) || '--'
              ),
              'Construction Type': isEditMode ? (
                <Autocomplete
                  options={constructionTypeOptions}
                  getOptionLabel={option => option.label}
                  defaultValue={constructionTypeOptions.find(option => option.label === project?.constructionType)}
                  onChange={(event, newValue) => onEditChange('constructionType', newValue ? newValue.label : '')}
                  renderInput={params => (
                    <CustomizedTextField
                      placeholder='Select an option'
                      {...params}
                      hasError={errorFieldNames.includes('Construction Type')}
                    />
                  )}
                />
              ) : (
                (project?.constructionType ?? '--')
              ),
              'Reviewing Subcontractor Lien Waivers': isEditMode ? (
                <Autocomplete
                  options={[
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                  ]}
                  getOptionLabel={option => option.label}
                  value={
                    project?.reviewingSubcontractorLienWaivers === true
                      ? { label: 'Yes', value: true }
                      : project?.reviewingSubcontractorLienWaivers === false
                        ? { label: 'No', value: false }
                        : null
                  }
                  onChange={(event, newValue) => {
                    onEditChange('reviewingSubcontractorLienWaivers', newValue ? newValue.value : null);
                  }}
                  renderInput={params => (
                    <CustomizedTextField
                      placeholder='Select an option'
                      {...params}
                      hasError={errorFieldNames.includes('Reviewing Subcontractor Lien Waivers')}
                    />
                  )}
                />
              ) : project?.reviewingSubcontractorLienWaivers === true ? (
                'Yes'
              ) : project?.reviewingSubcontractorLienWaivers === false ? (
                'No'
              ) : (
                '--'
              ),
              'Reviewing Construction Meeting Minutes': isEditMode ? (
                <Autocomplete
                  options={[
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                  ]}
                  getOptionLabel={option => option.label}
                  value={
                    project?.reviewingConstructionMeetingMinutes === true
                      ? { label: 'Yes', value: true }
                      : project?.reviewingConstructionMeetingMinutes === false
                        ? { label: 'No', value: false }
                        : null
                  }
                  onChange={(event, newValue) => {
                    onEditChange('reviewingConstructionMeetingMinutes', newValue ? newValue.value : null);
                  }}
                  renderInput={params => (
                    <CustomizedTextField
                      placeholder='Select an option'
                      {...params}
                      hasError={errorFieldNames.includes('Reviewing Construction Meeting Minutes')}
                    />
                  )}
                />
              ) : project?.reviewingConstructionMeetingMinutes === true ? (
                'Yes'
              ) : project?.reviewingConstructionMeetingMinutes === false ? (
                'No'
              ) : (
                '--'
              ),
              'Reviewing Potential Change Orders': isEditMode ? (
                <Autocomplete
                  options={[
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                  ]}
                  getOptionLabel={option => option.label}
                  value={
                    project?.reviewingPotentialChangeOrders === true
                      ? { label: 'Yes', value: true }
                      : project?.reviewingPotentialChangeOrders === false
                        ? { label: 'No', value: false }
                        : null
                  }
                  onChange={(event, newValue) => {
                    onEditChange('reviewingPotentialChangeOrders', newValue ? newValue.value : null);
                  }}
                  renderInput={params => (
                    <CustomizedTextField
                      placeholder='Select an option'
                      {...params}
                      hasError={errorFieldNames.includes('Reviewing Potential Change Orders')}
                    />
                  )}
                />
              ) : project?.reviewingPotentialChangeOrders === true ? (
                'Yes'
              ) : project?.reviewingPotentialChangeOrders === false ? (
                'No'
              ) : (
                '--'
              ),
              'Reviewing Plan Or Cost': isEditMode ? (
                <Autocomplete
                  options={[
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                  ]}
                  getOptionLabel={option => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  value={
                    project?.reviewingPlanOrCost === true
                      ? { label: 'Yes', value: true }
                      : project?.reviewingPlanOrCost === false
                        ? { label: 'No', value: false }
                        : null
                  }
                  onChange={(event, newValue) => {
                    onEditChange('reviewingPlanOrCost', newValue ? newValue.value : null);
                  }}
                  renderInput={params => (
                    <CustomizedTextField
                      placeholder='Select an option'
                      {...params}
                      hasError={errorFieldNames.includes('Reviewing Plan Or Cost')}
                    />
                  )}
                />
              ) : project?.reviewingPlanOrCost === true ? (
                'Yes'
              ) : project?.reviewingPlanOrCost === false ? (
                'No'
              ) : (
                '--'
              ),
              'Single Change Order Threshold': isEditMode ? (
                <CustomizedTextField
                  placeholder='0'
                  value={singleChangeOrderThresholdDraft}
                  onChange={e => setSingleChangeOrderThresholdDraft(e.target.value)}
                  onBlur={() => onEditChange('singleChangeOrderThreshold', singleChangeOrderThresholdDraft)}
                  InputProps={{
                    inputComponent: NumericFormatInput as any,
                  }}
                  hasError={errorFieldNames.includes('Single Change Order Threshold')}
                />
              ) : (
                formatCurrency(project?.singleChangeOrderThreshold?.toString() ?? '--')
              ),
              'Aggregate Change Order Threshold': isEditMode ? (
                <CustomizedTextField
                  placeholder='0'
                  value={aggregateChangeOrderThresholdDraft}
                  onChange={e => setAggregateChangeOrderThresholdDraft(e.target.value)}
                  onBlur={() => onEditChange('aggregateChangeOrderThreshold', aggregateChangeOrderThresholdDraft)}
                  InputProps={{
                    inputComponent: NumericFormatInput as any,
                  }}
                  hasError={errorFieldNames.includes('Aggregate Change Order Threshold')}
                />
              ) : (
                formatCurrency(project?.aggregateChangeOrderThreshold?.toString() ?? '--')
              ),
              'Commencement Date': isEditMode ? (
                <DynamicDatePicker
                  fieldName='commencementDate'
                  value={project?.commencementDate ?? null}
                  onChange={onEditChange}
                  hasError={errorFieldNames.includes('Commencement Date')}
                />
              ) : (
                formatISODate(project?.commencementDate) || '--'
              ),
              'Original Completion Date': isEditMode ? (
                <DynamicDatePicker
                  fieldName='originalCompletionDateEstimated'
                  value={project?.originalCompletionDateEstimated ?? null}
                  onChange={onEditChange}
                  hasError={errorFieldNames.includes('Original Completion Date')}
                />
              ) : (
                formatISODate(project?.originalCompletionDateEstimated) || '--'
              ),
              'Revised Completion Date': isEditMode ? (
                <DynamicDatePicker
                  fieldName='revisedCompletionDate'
                  value={project?.revisedCompletionDate ?? null}
                  onChange={onEditChange}
                  hasError={errorFieldNames.includes('Revised Completion Date')}
                />
              ) : (
                formatISODate(project?.revisedCompletionDate) || '--'
              ),
              'Bank Loan Expiration Date': isEditMode ? (
                <DynamicDatePicker
                  fieldName='bankLoanExpirationDate'
                  value={project?.bankLoanExpirationDate ?? null}
                  onChange={onEditChange}
                  hasError={errorFieldNames.includes('Bank Loan Expiration Date')}
                />
              ) : (
                formatISODate(project?.bankLoanExpirationDate) || '--'
              ),
              ...((project?.cdccEstimatedCompletionDate || isEditMode) && {
                'CDCC-Estimated Completion Date': isEditMode ? (
                  <DynamicDatePicker
                    fieldName='cdccEstimatedCompletionDate'
                    value={project?.cdccEstimatedCompletionDate ?? null}
                    onChange={onEditChange}
                    hasError={errorFieldNames.includes('CDCC-Estimated Completion Date')}
                  />
                ) : (
                  formatISODate(project?.cdccEstimatedCompletionDate)
                ),
              }),
            }}
            itemsPerRow={isScreenBelowMedium ? 2 : 4}
            isEditMode={isEditMode}
            showEditIcon={true}
            onEditClick={() => setIsEditMode(true)}
            errorFields={errorFieldNames}
            disableActions={readonly}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProjectSummary;
